import { useState } from 'react'
import { toast } from 'sonner'

import type { Vitals } from '@snootsvet/shared'

import { NumericInput } from '@/frontend/components/numeric-input'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Label } from '@/frontend/components/ui/label'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useCreateVitals } from '@/frontend/hooks/api/use-create-vitals'
import { usePatchVitals } from '@/frontend/hooks/api/use-patch-vitals'
import {
  DEFAULT_VITALS,
  getVitalsFields,
  hasAnyVital,
  type VitalsValues,
  vitalsFromServer,
  vitalsToApi,
} from '@/frontend/lib/vitals'

/**
 * Dialog form for recording or editing a standalone vitals entry (not linked to
 * a consultation). Pass `vitals` to edit an existing record; omit it to record a
 * new one. Saving is blocked until at least one measurement is entered.
 */
export function VitalsFormDialog({
  patientId,
  vitals,
  open,
  onOpenChange,
}: {
  patientId: string
  vitals?: Vitals
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { region } = useViewerRegion()
  const fields = getVitalsFields(region)
  const initialValues = () => (vitals ? vitalsFromServer(vitals, region) : DEFAULT_VITALS)
  const [values, setValues] = useState<VitalsValues>(initialValues)
  const { mutate: createVitals, isPending: isCreating } = useCreateVitals()
  const { mutate: patchVitals, isPending: isPatching } = usePatchVitals()

  const isEditing = vitals != null
  const isPending = isCreating || isPatching
  const canSave = hasAnyVital(values)

  function handleOpenChange(next: boolean) {
    // Reset to the initial state when closing so a reopened dialog is clean.
    if (!next) setValues(initialValues())
    onOpenChange(next)
  }

  function handleSubmit() {
    if (!canSave) return
    const onError = (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Failed to save vitals')

    if (isEditing) {
      patchVitals(
        { id: vitals.id, body: vitalsToApi(values, region) },
        { onSuccess: () => onOpenChange(false), onError }
      )
    } else {
      createVitals(
        { patientId, source: 'clinical', ...vitalsToApi(values, region) },
        { onSuccess: () => handleOpenChange(false), onError }
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Vitals' : 'Record Vitals'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
          {fields.map(({ key, label, unit }) => (
            <div key={key} className="flex flex-col gap-1">
              <Label className="text-sm text-muted-foreground">
                {label}
                <span className="ml-1 font-normal text-muted-foreground/60">{unit}</span>
              </Label>
              <NumericInput
                value={values[key]}
                onChange={(val) => setValues((prev) => ({ ...prev, [key]: val }))}
                placeholder="—"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="lg" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button size="lg" onClick={handleSubmit} disabled={isPending || !canSave}>
            {isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
